import React from 'react';
import _ from 'platform/utilities/data';
import { DATA_PATHS } from '../constants';
import {
  getBddShaUploads,
  isUploadingBddSha,
  isBDD,
  isEvidenceEnhancement,
} from '../utils';

const SECTION_CONFIGURATIONS = {
  'bdd-sha-uploads': {
    evidenceIdKey: 'confirmationCode',
    evidenceNameKey: 'name',
    isSelected: isUploadingBddSha,
    getEvidences: getBddShaUploads,
    getHeaderText: shouldEnhance =>
      shouldEnhance
        ? 'We’ll submit the Separation Health Assessment you uploaded'
        : 'We’ll submit the Separation Health Assessment document that you uploaded',
  },
};

const NO_EVIDENCE_MESSAGE = (
  <p>
    You haven’t uploaded any evidence. This may delay us processing your claim.
    In addition, we may also schedule a claim exam for you to help us decide
    your claim.
  </p>
);

const SECTION_ORDERS = {
  UNENHANCED: [
    'bdd-sha-uploads',
    'va-evidence',
    'private-evidence-uploads',
    'private-facilities',
    'service-treatment-records-attachments',
    'additional-documents',
  ],
  ENHANCED: [
    'bdd-sha-uploads',
    'service-treatment-records-attachments',
    'va-evidence',
    'private-evidence-uploads',
    'private-facilities',
    'additional-documents',
  ],
};

const buildSectionsList = (formData, { shouldEnhance }) => {
  const sectionsList = [];
  const sectionOrder = shouldEnhance
    ? SECTION_ORDERS.ENHANCED
    : SECTION_ORDERS.UNENHANCED;

  for (const sectionId of sectionOrder) {
    const configuration = SECTION_CONFIGURATIONS[sectionId];

    if (configuration) {
      const headerText = `${configuration.getHeaderText(shouldEnhance)}:`;
      const evidences = configuration.isSelected(formData)
        ? configuration.getEvidences(formData)
        : [];

      if (evidences.length) {
        const evidencesList = (
          <ul>
            {evidences.map((evidence, index) => {
              const key =
                evidence[configuration.evidenceIdKey] ||
                evidence[configuration.evidenceNameKey] ||
                index;
              const name = evidence[configuration.evidenceNameKey];
              return <li key={key}>{name}</li>;
            })}
          </ul>
        );

        const section = shouldEnhance ? (
          <div key={sectionId} className="vads-u-margin-top--2">
            <strong>{headerText}</strong>
            {evidencesList}
          </div>
        ) : (
          <div key={sectionId}>
            <p>{headerText}</p>
            {evidencesList}
          </div>
        );

        sectionsList.push(section);
      }
    }
  }

  return sectionsList;
};

export const summaryOfEvidenceDescription = ({ formData }) => {
  const shouldEnhance = isEvidenceEnhancement(formData);
  const sectionsList = buildSectionsList(formData, {
    shouldEnhance,
  });
  const vaEvidence = _.get('vaTreatmentFacilities', formData, []);
  const privateEvidence = _.get('providerFacility', formData, []);
  const privateEvidenceUploads = _.get(
    'privateMedicalRecordAttachments',
    formData,
    [],
  );
  const serviceTreatmentRecordsUploads = _.get(
    'serviceTreatmentRecordsAttachments',
    formData,
    [],
  );
  const layEvidenceUploads = _.get('additionalDocuments', formData, []);
  const evidenceLength = !!vaEvidence.concat(
    privateEvidence,
    privateEvidenceUploads,
    serviceTreatmentRecordsUploads,
    layEvidenceUploads,
  ).length;
  const selectedEvidence = _.get('view:hasEvidence', formData, false);
  const hasMedicalRecords = _.get('view:hasMedicalRecords', formData, false);
  const serviceTreatmentRecordsSelected = _.get(
    'view:uploadServiceTreatmentRecordsQualifier.view:hasServiceTreatmentRecordsToUpload',
    formData,
    false,
  );
  const hasSeparationHealthAssessment = _.get(
    'view:hasSeparationHealthAssessment',
    formData,
    false,
  );
  // Legacy/non-enhanced flow: show NO_EVIDENCE_MESSAGE when either
  // - there's truly no evidence, or
  // - the user explicitly chose "no" for evidence and STR, even if stale uploads remain
  const legacyNoEvidence =
    !shouldEnhance &&
    !sectionsList.length &&
    (!evidenceLength ||
      (!selectedEvidence && !serviceTreatmentRecordsSelected));

  // Enhanced, non-BDD flow: show NO_EVIDENCE_MESSAGE only when there is truly no evidence
  const enhancedNoEvidenceNonBdd =
    shouldEnhance &&
    !isBDD(formData) &&
    !hasMedicalRecords &&
    layEvidenceUploads.length === 0;

  const bddBothSubmitLater =
    isBDD(formData) &&
    selectedEvidence === false &&
    serviceTreatmentRecordsSelected === false &&
    !hasSeparationHealthAssessment;
  // Evidence isn't always properly cleared out from form data if removed so
  // need to also check that 'no evidence' was explicitly selected
  // TODO: refactor logic for this content when removing current flow
  if (legacyNoEvidence) {
    return NO_EVIDENCE_MESSAGE;
  }

  let vaContent = null;
  let privateContent = null;
  let layContent = null;
  let privateEvidenceContent = null;
  let serviceTreatmentRecordsContent = null;

  const vaEvidenceSelected = _.get(DATA_PATHS.hasVAEvidence, formData, false);
  const privateEvidenceSelected = _.get(
    DATA_PATHS.hasPrivateEvidence,
    formData,
    false,
  );
  const uploadPrivateEvidenceSelected = _.get(
    DATA_PATHS.hasPrivateRecordsToUpload,
    formData,
    false,
  );

  const additionalEvidenceSelected = _.get(
    DATA_PATHS.hasAdditionalDocuments,
    formData,
    false,
  );

  if (vaEvidence.length && vaEvidenceSelected) {
    const facilitiesList = vaEvidence.map((facility, index) => (
      <li key={index}>{facility.treatmentCenterName}</li>
    ));
    vaContent = shouldEnhance ? (
      <div className="vads-u-margin-top--2">
        <strong>
          We’ll request your VA medical records on your behalf from these VA
          medical centers:
        </strong>
        <ul>{facilitiesList}</ul>
      </div>
    ) : (
      <div>
        <p>We’ll get your VA medical records from:</p>
        <ul>{facilitiesList}</ul>
      </div>
    );
  }

  if (
    privateEvidenceUploads.length &&
    privateEvidenceSelected &&
    uploadPrivateEvidenceSelected
  ) {
    const privateEvidenceUploadsList = privateEvidenceUploads.map(upload => (
      <li key={upload.name}>{upload.name}</li>
    ));
    privateContent = shouldEnhance ? (
      <div className="vads-u-margin-top--2">
        <strong>
          We’ll submit these private medical records you uploaded:
        </strong>
        <ul>{privateEvidenceUploadsList}</ul>
      </div>
    ) : (
      <div>
        <p>We’ll submit the below private medical records you uploaded:</p>
        <ul>{privateEvidenceUploadsList}</ul>
      </div>
    );
  }

  if (
    privateEvidence.length &&
    privateEvidenceSelected &&
    !uploadPrivateEvidenceSelected
  ) {
    const privateEvidenceList = privateEvidence.map(facility => (
      <li key={facility.providerFacilityName}>
        {facility.providerFacilityName}
      </li>
    ));
    privateEvidenceContent = shouldEnhance ? (
      <div className="vads-u-margin-top--2">
        <strong>
          We’ll request your private medical records on your behalf from these
          medical centers:
        </strong>
        <ul>{privateEvidenceList}</ul>
      </div>
    ) : (
      <div>
        <p>We’ll get your non-VA treatment records from:</p>
        <ul>{privateEvidenceList}</ul>
      </div>
    );
  }

  if (
    serviceTreatmentRecordsUploads.length &&
    serviceTreatmentRecordsSelected
  ) {
    const serviceTreatmentRecordsUploadsList =
      serviceTreatmentRecordsUploads.map(upload => (
        <li key={upload.name}>{upload.name}</li>
      ));
    serviceTreatmentRecordsContent = shouldEnhance ? (
      <div className="vads-u-margin-top--2">
        <strong>
          We’ll submit these service treatment records you uploaded:
        </strong>
        <ul>{serviceTreatmentRecordsUploadsList}</ul>
      </div>
    ) : (
      <div>
        <p>We’ll submit the below service treatment records you uploaded:</p>
        <ul>{serviceTreatmentRecordsUploadsList}</ul>
      </div>
    );
  }

  if (layEvidenceUploads.length && additionalEvidenceSelected) {
    const layEvidenceUploadsList = layEvidenceUploads.map(upload => (
      <li key={upload.name}>{upload.name}</li>
    ));
    layContent = shouldEnhance ? (
      <div className="vads-u-margin-top--2">
        <strong>We’ll submit these documents you uploaded:</strong>
        <ul>{layEvidenceUploadsList}</ul>
      </div>
    ) : (
      <div>
        <p>We’ll submit the below supporting evidence you uploaded:</p>
        <ul>{layEvidenceUploadsList}</ul>
      </div>
    );
  }

  return (
    <div className="vads-u-margin-top--3">
      {(bddBothSubmitLater || enhancedNoEvidenceNonBdd) && NO_EVIDENCE_MESSAGE}
      {!bddBothSubmitLater &&
        !enhancedNoEvidenceNonBdd &&
        (evidenceLength || sectionsList.length > 0) &&
        shouldEnhance && <p>You provided documents to support your claim.</p>}
      {sectionsList}
      {vaContent}
      {privateContent}
      {privateEvidenceContent}
      {serviceTreatmentRecordsContent}
      {layContent}
      {shouldEnhance && (
        <p>Next, we’ll tell you what to expect during a claim exam.</p>
      )}
    </div>
  );
};
